/** Pre-trip item category → General tab. Display name (→`name`), system name
 *  (→`system_name`), rendering type Select (1-4), and a read-only item count.
 *  Categories have no optimistic-concurrency token (last-writer-wins, like
 *  regions). System-default categories (`is_system`) are read-only — the form
 *  disables every control and hides the save bar. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type { PreTripItemTypeRow, PreTripItemTypeWriteInput, RenderingType } from '@/api/preTripItemTypes';
import { RENDERING_TYPE_LABELS } from '@/api/preTripItemTypes';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { categoryItemsActions, copyActions, type CatalogCtx } from '../../catalog/catalogLinks';
import { usePreTripItemTypePatch } from '../hooks/usePreTripItemTypePatch';

const RENDERING_TYPES: RenderingType[] = [1, 2, 3, 4];

const Schema = z.object({
  name: z.string().trim().min(1, 'Display name is required'),
  system_name: z.string().trim().min(1, 'System name is required'),
  rendering_type: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
});

type FormValues = z.infer<typeof Schema>;

function toDefaults(t: PreTripItemTypeRow): FormValues {
  return { name: t.name, system_name: t.system_name, rendering_type: t.rendering_type };
}

/** Pull a field-specific message out of a DRF 400 body ({field: [msg]}). */
function fieldError(body: Record<string, unknown> | null, key: string): string | undefined {
  const v = body?.[key];
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  if (typeof v === 'string') return v;
  return undefined;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function PreTripItemCategoryGeneralTab({ type, onSaved }: { type: PreTripItemTypeRow; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  // System-default categories are read-only server-side; disable the whole form.
  const canManage = hasPerm('console_pretrip_manage') && !type.is_system;
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(type),
  });
  const patch = usePreTripItemTypePatch(type, onSaved);

  useEffect(() => {
    reset(toDefaults(type));
    // Re-seed when the server record changes (incl. after a save → refetch, so the
    // form isn't stuck "dirty"). Categories have no updated_ts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const submit = handleSubmit((v) => {
    const body: PreTripItemTypeWriteInput = {
      name: v.name.trim(),
      system_name: v.system_name.trim(),
      rendering_type: v.rendering_type,
    };
    patch.save(body);
  });

  const err = formState.errors;
  const nameServerError = fieldError(patch.fieldErrors, 'name');
  const systemNameServerError = fieldError(patch.fieldErrors, 'system_name');
  const ctx: CatalogCtx = { tenantId: type.tenant?.id };

  return (
    <form onSubmit={submit}>
      {type.is_system && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This is a shared system-default category and is read-only.
        </Typography>
      )}

      <Section title="Basics">
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Display name"
            error={!!err.name || !!nameServerError}
            helperText={err.name?.message || nameServerError} />
        )} />
        <Controller control={control} name="system_name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="System name"
            error={!!err.system_name || !!systemNameServerError}
            helperText={err.system_name?.message || systemNameServerError}
            InputProps={{
              endAdornment: (
                <FieldActionsAdornment
                  label="Copy the system name"
                  actions={copyActions(field.value, 'Copy system name')}
                />
              ),
            }} />
        )} />
        <Controller control={control} name="rendering_type" render={({ field }) => (
          <TextField select size="small" sx={{ width: 260 }} disabled={!canManage}
            label="Type" value={field.value}
            onChange={(e) => field.onChange(Number(e.target.value) as RenderingType)}>
            {RENDERING_TYPES.map((rt) => (
              <MenuItem key={rt} value={rt}>{RENDERING_TYPE_LABELS[rt]}</MenuItem>
            ))}
          </TextField>
        )} />
      </Section>

      <Section title="Items">
        <Typography variant="body2" color="text.secondary">
          Items in this category:{' '}
          <ValueActions actions={categoryItemsActions(type.id, ctx)}>{type.item_count}</ValueActions>
        </Typography>
      </Section>

      {canManage && (
        <EditorSaveBar
          dirty={formState.isDirty} saving={patch.saving} canManage={canManage}
          onSave={submit} onDiscard={() => reset(toDefaults(type))}
        />
      )}
    </form>
  );
}
